import { useOpenSCAD } from '@/hooks/useOpenSCAD';
import {
  hasSyntaxError,
  preprocessScadModifiers,
  parseConstraints,
  type JsonProjectFile,
  type ConstraintDef,
} from '@/lib/scad-preprocessor';
import { useConstraintCheck } from '@/hooks/useConstraintCheck';
import { ConstraintWarningPanel } from '@/components/viewer/ConstraintWarningPanel';
import {
  resolveApiIncludes,
  parseApiIncludes,
} from '@/lib/api-include-fetcher';
import {
  normalizeScadCode,
  hashScadCode,
  geometryHash,
} from '@/lib/scad-normalizer';
import { parseSceneExtensions } from '@/lib/scad-scene-parser';
import { applyCodeSubstitutions } from '@/utils/parameters/codeSubstitution';
import { useEffect, useRef, useState } from 'react';
import { ThreeScene } from '@/components/viewer/ThreeScene';
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  FileX,
  Shapes,
  Terminal,
} from 'lucide-react';
import OpenSCADError from '@/lib/OpenSCADError';
import { useBlob } from '@/contexts/BlobContext';
import { useSceneSettings } from '@/contexts/SceneSettingsContext';
import { usePerformanceStore } from '@/stores/performanceStore';
import type { ProjectFile } from '@/components/files/FileManager';
import { useGeometryLifecycle } from '@/components/viewer/model/useGeometryLifecycle';
import {
  parseOpenSCADErrors,
  type ParsedOpenSCADErrors,
  type OpenSCADErrorEntry,
} from '@/lib/parseOpenSCADErrors';
import type { SerializedOpenSCADError } from '@/worker/types';

interface OpenSCADViewerProps {
  scadCode: string;
  projectFiles?: ProjectFile[];
  onError?: (error: OpenSCADError | Error | null) => void;
  onJumpToLine?: (line: number) => void;
  onFitToBoundsReady?: (fitFn: () => void) => void;
  /** Whether auto-fit is currently enabled (initial fit or user toggle) */
  autoFit?: boolean;
  /** Whether the next auto-fit should reset camera direction (first fit only) */
  isInitialFit?: boolean;
  /** Called after an auto-fit completes */
  onFitComplete?: () => void;
  /** When true, enables presentation mode and minimal toolbar in the 3D scene */
  productMode?: boolean;
  /** When true, hides all toolbar/chrome; full-quality auto-rotating viewer */
  showcaseMode?: boolean;
  /** Horizontal pixel offset to shift the visual center (for overlay panels) */
  cameraOffsetX?: number;
  /**
   * Called once when the first compile result (geometry) is produced.
   * Used by blog playgrounds to release the init slot so the next
   * queued playground can begin its own initialisation.
   */
  onFirstResult?: () => void;
  /**
   * Externally controlled presentation mode (auto-rotation).
   * When provided, the ThreeScene syncs its internal state to this value.
   */
  presentationMode?: boolean;
  /** OpenSCAD parameters — forwarded to JS texture scripts as `params` */
  parameters?: import('@shared/types').Parameter[];
  /**
   * Optional controlled viewer state for command-palette integration.
   * When provided, ThreeScene uses this value instead of its internal state.
   */
  viewerState?: import('@/components/viewer/toolbar/ViewerToolbar').ViewerState;
  onViewerStateChange?: (
    updater: (
      prev: import('@/components/viewer/toolbar/ViewerToolbar').ViewerState,
    ) => import('@/components/viewer/toolbar/ViewerToolbar').ViewerState,
  ) => void;
  /** Forwarded to ThreeScene — allows context-menu actions to append code to the editor */
  onInsertCode?: (code: string) => void;
  /** Forwarded to ThreeScene — allows PiP drag-drop to update existing code in the editor */
  onReplaceCode?: (search: string | RegExp, replacement: string) => void;
  /**
   * Called when the user clicks a geometry region wrapped with interactive()
   * in the SCAD source. Forwarded to ThreeScene → ModelRenderer.
   */
  onInteraction?: (config: import('@shared/types').InteractiveConfig) => void;
  /**
   * Called whenever the set of compiled colored geometries changes.
   * Used by the parent (ViewerSection) to display per-color visibility toggles.
   */
  onColorsDetected?: (geometries: import('@/components/viewer/types').ColoredGeometry[]) => void;
  /**
   * Set of color keys (from toColorKey()) that should be hidden in the preview.
   * Forwarded to ThreeScene for filtering without recompilation.
   */
  hiddenColorKeys?: Set<string>;
  /**
   * Increment this value to force a recompile even when the code hasn't changed
   * (e.g. triggered by the F5 keyboard shortcut). The compile hash guards are
   * bypassed whenever this counter changes.
   */
  forceRecompileKey?: number;
  /**
   * Called once the pub/sub subscribe function is ready. Forwarded to ThreeScene.
   * Allows external callers (e.g. ParameterSection) to subscribe to pub/sub keys.
   */
  onSubscribeReady?: (
    subscribe: (key: string, callback: (value: unknown) => void) => () => void,
  ) => void;
  /**
   * Called once the pub/sub publish function is ready. Forwarded to ThreeScene.
   * Allows external callers (e.g. ParameterSection) to push values into the channel.
   */
  onPubSubPublish?: (publish: (key: string, value: unknown) => void) => void;
  /**
   * Called whenever the effective camera path (JS-parsed) changes.
   * Used by the parent to read cameraPath for the product readiness score
   * without waiting for a full WASM compile.
   */
  onCameraPathChange?: (path: import('@/worker/types').CameraPathWaypoint[]) => void;
}

function isSerializedOpenSCADError(
  err: unknown,
): err is SerializedOpenSCADError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as SerializedOpenSCADError).name === 'OpenSCADError' &&
    Array.isArray((err as SerializedOpenSCADError).stdErr)
  );
}

function categoryIcon(category: ParsedOpenSCADErrors['category']) {
  switch (category) {
    case 'Syntax Error':
      return <AlertCircle className="h-3.5 w-3.5 shrink-0" />;
    case 'Geometry Error':
      return <Shapes className="h-3.5 w-3.5 shrink-0" />;
    case 'Missing File':
      return <FileX className="h-3.5 w-3.5 shrink-0" />;
    case 'Warning':
      return <AlertTriangle className="h-3.5 w-3.5 shrink-0" />;
    default:
      return <Terminal className="h-3.5 w-3.5 shrink-0" />;
  }
}

interface ErrorPanelProps {
  parsed: ParsedOpenSCADErrors;
  rawStdErr: string[];
  onJumpToLine?: (line: number) => void;
}

function ErrorPanel({ parsed, rawStdErr, onJumpToLine }: ErrorPanelProps) {
  const [rawExpanded, setRawExpanded] = useState(false);

  const handleEntryClick = (entry: OpenSCADErrorEntry) => {
    if (entry.line !== undefined && onJumpToLine) {
      onJumpToLine(entry.line);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {/* Header row: category icon + label */}
      <div className="flex items-center gap-1.5 font-bold">
        {categoryIcon(parsed.category)}
        <span>{parsed.category}</span>
      </div>

      {/* Summary */}
      <p className="break-words font-mono text-xs leading-snug opacity-90">
        {parsed.summary}
      </p>

      {/* Parsed entries (if more than just the summary line) */}
      {parsed.entries.length > 0 && (
        <div className="mt-0.5 flex flex-col gap-0.5">
          {parsed.entries.map((entry, i) => (
            <button
              key={`${entry.severity}-${entry.line ?? i}-${entry.message}`}
              type="button"
              disabled={entry.line === undefined || !onJumpToLine}
              className={[
                'flex w-full items-start gap-1.5 rounded px-1.5 py-1 text-left font-mono text-xs leading-snug',
                entry.severity === 'warning'
                  ? 'bg-yellow-500/20'
                  : 'bg-white/10',
                entry.line !== undefined && onJumpToLine
                  ? 'cursor-pointer transition-colors hover:bg-white/20'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleEntryClick(entry)}
              title={
                entry.line !== undefined && onJumpToLine
                  ? `Jump to line ${entry.line}`
                  : undefined
              }
            >
              {entry.severity === 'warning' ? (
                <AlertTriangle className="mt-px h-3 w-3 shrink-0 text-yellow-300" />
              ) : (
                <AlertCircle className="mt-px h-3 w-3 shrink-0" />
              )}
              <span className="min-w-0 break-words">
                {entry.line !== undefined && (
                  <span className="mr-1 opacity-60">
                    line {entry.line}
                    {entry.column !== undefined ? `:${entry.column}` : ''}:
                  </span>
                )}
                {entry.message}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Raw log toggle */}
      {rawStdErr.length > 0 && (
        <div className="mt-1">
          <button
            className="flex items-center gap-1 text-xs opacity-60 transition-opacity hover:opacity-100"
            onClick={() => setRawExpanded((v) => !v)}
          >
            {rawExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Raw output
          </button>
          {rawExpanded && (
            <pre className="scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent mt-1 max-h-[150px] overflow-auto whitespace-pre-wrap break-words rounded bg-black/20 px-2 py-1.5 font-mono text-xs opacity-70">
              {rawStdErr.join('\n')}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// Stable empty array used when no projectFiles are provided, preventing
// the default `= []` parameter from creating a new reference on every render
// and causing the compile effect to loop infinitely.
const EMPTY_PROJECT_FILES: ProjectFile[] = [];

export function OpenSCADViewer({
  scadCode,
  projectFiles,
  onError,
  onJumpToLine,
  onFitToBoundsReady,
  autoFit = true,
  isInitialFit = true,
  onFitComplete,
  productMode = false,
  showcaseMode = false,
  cameraOffsetX = 0,
  onFirstResult,
  presentationMode: externalPresentationMode,
  parameters,
  viewerState: controlledViewerState,
  onViewerStateChange: onControlledViewerStateChange,
  onInsertCode,
  onReplaceCode,
  onInteraction,
  forceRecompileKey,
  onColorsDetected,
  hiddenColorKeys,
  onSubscribeReady,
  onPubSubPublish,
  onCameraPathChange,
}: OpenSCADViewerProps) {
  const stableProjectFiles = projectFiles ?? EMPTY_PROJECT_FILES;

  /**
   * Session-scoped cache for API include responses.
   * Stored in a ref so it survives re-renders but is reset when the component
   * unmounts (i.e. when the user closes or navigates away from the editor).
   * Map<url, rawJsonText>
   */
  const apiCacheRef = useRef<Map<string, string>>(new Map());

  /**
   * Fully preprocessed SCAD code, ready for the syntax checker and WASM compiler.
   * Updated asynchronously whenever `scadCode` or `stableProjectFiles` change.
   * Starts as an empty string so no compile is attempted until preprocessing completes.
   */
  const [processedScadCode, setProcessedScadCode] = useState('');

  // ---------------------------------------------------------------------------
  // Change-detection refs for compile skip optimizations
  // ---------------------------------------------------------------------------

  /**
   * FNV-1a hash of the last normalized (comment-stripped) processedScadCode
   * that was dispatched to the WASM compiler. Used to skip recompile when only
   * comments changed.
   * Initialized to NaN so the very first code always compiles.
   */
  const prevNormalizedHashRef = useRef<number>(NaN);

  /**
   * FNV-1a hash of the last processedScadCode with all Meshpit scene-extension
   * calls (light_*, camera_pip, scene_*) stripped, in addition to comments.
   * Used to skip full WASM recompile when only lights / PiP / scene settings
   * changed — the JS scene parser handles those updates immediately.
   * Initialized to NaN so the very first code always compiles.
   */
  const prevGeometryHashRef = useRef<number>(NaN);

  /**
   * JS-parsed lights, PiP cameras, and scene overrides derived directly from
   * the raw SCAD source without a WASM round-trip. These are applied
   * immediately to the Three.js scene when scadCode changes. The authoritative
   * WASM-confirmed values (from useOpenSCAD) override them once a compile
   * finishes.
   */
  const [jsParsedLights, setJsParsedLights] = useState<
    import('@/worker/types').SceneLight[]
  >([]);
  const [jsParsedPipCameras, setJsParsedPipCameras] = useState<
    import('@/worker/types').PiPCamera[]
  >([]);
  const [jsParsedCameraPath, setJsParsedCameraPath] = useState<
    import('@/worker/types').CameraPathWaypoint[]
  >([]);
  const [jsParsedSvgOverlays, setJsParsedSvgOverlays] = useState<
    import('@/worker/types').SvgOverlay[]
  >([]);
  const [jsParsedSceneOverrides, setJsParsedSceneOverrides] = useState<
    import('@/worker/types').SceneOverrides
  >({});

  /**
   * True while API includes are being fetched (prevents premature compilation).
   */
  const [isFetchingApi, setIsFetchingApi] = useState(false);

  /**
   * Errors from the last round of API include fetches, surfaced to the user.
   */
  const [apiFetchErrors, setApiFetchErrors] = useState<string[]>([]);

  // ---------------------------------------------------------------------------
  // JS-side scene parser: runs immediately on every raw code change.
  // Extracts lights, PiP cameras, and scene overrides without WASM.
  // This gives instant Three.js scene updates for these declarations.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const { lights, pipCameras, cameraPath, svgOverlays, sceneOverrides } =
      parseSceneExtensions(scadCode);
    setJsParsedLights(lights);
    setJsParsedPipCameras(pipCameras);
    setJsParsedCameraPath(cameraPath);
    setJsParsedSvgOverlays(svgOverlays);
    setJsParsedSceneOverrides(sceneOverrides);
  }, [scadCode]);

  // Resolve API includes async, then run the synchronous preprocessor.
  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      const hasApiIncludes = parseApiIncludes(scadCode).length > 0;

      if (hasApiIncludes) {
        setIsFetchingApi(true);
        setApiFetchErrors([]);
      }

      try {
        // Fetch any URL-form includes (cache hits return immediately).
        const { files: apiFiles, errors } = await resolveApiIncludes(
          scadCode,
          apiCacheRef.current,
        );

        if (cancelled) return;

        if (errors.length > 0) {
          setApiFetchErrors(
            errors.map((e) => `include <${e.url}>: ${e.error}`),
          );
        }

        // Merge fetched API files with uploaded project files.
        // API files are typed as JsonProjectFile (string content only) so they
        // can be concatenated with the broader ProjectFile list safely.
        const allFiles: JsonProjectFile[] = [
          ...stableProjectFiles,
          ...apiFiles,
        ];

        const processed = preprocessScadModifiers(scadCode, allFiles);
        setProcessedScadCode(processed);
      } finally {
        if (!cancelled) setIsFetchingApi(false);
      }
    }

    resolve();

    return () => {
      cancelled = true;
    };
  }, [scadCode, stableProjectFiles]);

  const { setBlob, setColoredBlobs, coloredBlobs } = useBlob();
  const {
    compileScad,
    isCompiling,
    output,
    coloredOutputs,
    isError,
    error,
    progress,
    lights,
    pipCameras,
    cameraPath,
    sceneOverrides,
    clones,
  } = useOpenSCAD({ onFirstResult });

  // ---------------------------------------------------------------------------
  // Constraint annotations — parsed from source, checked against compiled geometry.
  // ---------------------------------------------------------------------------

  /**
   * Constraint definitions parsed from `// @constraint` comments in the raw
   * SCAD source. Re-parsed whenever `scadCode` changes (pure JS, instant).
   */
  const [constraints, setConstraints] = useState<ConstraintDef[]>([]);

  useEffect(() => {
    setConstraints(parseConstraints(scadCode));
  }, [scadCode]);

  /**
   * Active constraint violations checked against the last successful compile.
   * `coloredBlobs` from BlobContext is only updated on a successful compile,
   * so violations automatically persist across failed compiles.
   */
  const { violations: constraintViolations } = useConstraintCheck(
    coloredBlobs,
    constraints,
  );

  // ---------------------------------------------------------------------------
  // Push scene_* overrides and lights into the shared context.
  //
  // Priority (highest first):
  //   1. WASM-confirmed values (from useOpenSCAD) — authoritative, set after compile
  //   2. JS-parsed values (from parseSceneExtensions) — instant preview, set on keystroke
  //
  // For lights: WASM result wins when available (it also captures colors/materials
  // that the JS parser doesn't handle). JS-parsed lights are used as a fast
  // preview until the first compile completes for this code version.
  //
  // hasWasmLightsRef tracks whether the most recent compile produced a WASM
  // lights result — when the geometry hash changes (new compile needed), it is
  // reset to false so JS-parsed values take over immediately again.
  // ---------------------------------------------------------------------------
  const { setSceneOverrides, setSceneLights } = useSceneSettings();

  /**
   * True when the currently-displayed lights/overrides came from a completed
   * WASM compile. Reset to false when a new geometry-changing compile is
   * dispatched, so the JS-parsed values immediately take over as the preview.
   */
  const wasmResultFreshRef = useRef(false);

  // When WASM compile finishes with new lights/overrides, push them to context
  // and mark the result as fresh.
  const prevOverridesRef = useRef<typeof sceneOverrides>(sceneOverrides);
  if (prevOverridesRef.current !== sceneOverrides) {
    prevOverridesRef.current = sceneOverrides;
    setSceneOverrides(sceneOverrides);
    wasmResultFreshRef.current = true;
  }
  const prevLightsRef = useRef<typeof lights>(lights);
  if (prevLightsRef.current !== lights) {
    prevLightsRef.current = lights;
    setSceneLights(lights);
    wasmResultFreshRef.current = true;
  }

  // When JS-parsed values change AND the WASM result is not fresh (i.e. we are
  // in the window between a code edit and the next compile completing), push the
  // JS-parsed values to context immediately for a responsive scene update.
  const prevJsLightsRef = useRef(jsParsedLights);
  const prevJsOverridesRef = useRef(jsParsedSceneOverrides);
  if (
    (prevJsLightsRef.current !== jsParsedLights ||
      prevJsOverridesRef.current !== jsParsedSceneOverrides) &&
    !wasmResultFreshRef.current
  ) {
    prevJsLightsRef.current = jsParsedLights;
    prevJsOverridesRef.current = jsParsedSceneOverrides;
    setSceneLights(jsParsedLights);
    setSceneOverrides(jsParsedSceneOverrides);
  }

  // Effective PiP cameras: always use the JS-parsed values, which are updated
  // synchronously on every code change (including scene-extension-only edits
  // like PiP drag-and-drop that skip the WASM compile). Fall back to WASM
  // pipCameras only when the JS parser hasn't produced anything yet.
  const effectivePipCameras =
    jsParsedPipCameras.length > 0 ? jsParsedPipCameras : pipCameras;

  // Effective camera path: same priority scheme as PiP cameras.
  // JS-parsed values are used immediately on code change; WASM values win once available.
  const effectiveCameraPath =
    jsParsedCameraPath.length > 0 ? jsParsedCameraPath : cameraPath;

  // Notify parent whenever the effective camera path changes (used for product score).
  useEffect(() => {
    onCameraPathChange?.(effectiveCameraPath);
  }, [effectiveCameraPath, onCameraPathChange]);

  // --- Geometry lifecycle: parsing, cancellation, disposal ---
  const { geometry, coloredGeometries } = useGeometryLifecycle(
    output,
    coloredOutputs,
  );

  // Notify parent whenever the colored geometry set changes so it can update
  // the per-color visibility toggles in the header bar.
  useEffect(() => {
    onColorsDetected?.(coloredGeometries);
  }, [coloredGeometries, onColorsDetected]);

  // Keep a ref to the latest compileScad so the effect below doesn't need it
  // as a dependency (compileScad identity is stable, but belt-and-suspenders).
  const compileScadRef = useRef(compileScad);
  compileScadRef.current = compileScad;

  // Keep a ref to the latest parameters so code substitutions can be applied
  // inside the compile effect without adding parameters as a dep (which would
  // re-trigger the compile on every parameter value change — the code change
  // is already the trigger).
  const parametersRef = useRef(parameters ?? []);
  parametersRef.current = parameters ?? [];

  // When forceRecompileKey changes (e.g. via F5 shortcut), reset the hash refs
  // so the compile effect bypasses its skip-if-unchanged guards and always runs.
  useEffect(() => {
    if (forceRecompileKey === undefined) return;
    prevNormalizedHashRef.current = NaN;
    prevGeometryHashRef.current = NaN;
    // The compile effect depends on processedScadCode; re-running it requires a
    // state update. We trigger a no-op re-process by re-setting the same value.
    setProcessedScadCode((prev) => prev);
  }, [forceRecompileKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert project files to worker format and compile.
  // Skip compilation when the parser detects a syntax error so we don't
  // waste a WASM round-trip (and potentially show a stale model) while
  // the user is mid-edit on broken code.
  // Also skip while API includes are still being fetched.
  // Note: we use processedScadCode (JSON includes already inlined) for both
  // the syntax check and the compile call so the WASM binary only ever sees
  // valid SCAD (never raw JSON include directives).
  useEffect(() => {
    if (isFetchingApi) return;
    if (processedScadCode && !hasSyntaxError(processedScadCode)) {
      // -----------------------------------------------------------------------
      // Optimization 1: Comment-only edit guard
      //
      // Normalize the processed code (strip comments + collapse blank lines)
      // and hash it. If the hash matches the previous compile, nothing
      // geometry-relevant changed — skip WASM entirely.
      //
      // NOTE: We NEVER send the normalized code to WASM. The original
      // processedScadCode (with comments) is always what the compiler sees,
      // so error line numbers remain accurate against the source in the editor.
      //
      // Non-JSON project files (imported .scad, .stl, .svg, etc.) are written
      // to the WASM virtual FS and affect the compiled output, but their content
      // is NOT reflected in processedScadCode. We therefore mix a hash of those
      // files' contents into the change-detection hash so that edits to imported
      // files always trigger a recompile.
      // -----------------------------------------------------------------------
      const normalized = normalizeScadCode(processedScadCode);

      // Build a combined hash that covers both the main processed code AND all
      // non-JSON project file contents. This ensures edits to imported files
      // (e.g. a helper .scad module) invalidate the hash and trigger a recompile.
      const workerFilesForHash = stableProjectFiles.filter(
        (f) => !f.filename.toLowerCase().endsWith('.json'),
      );
      let combinedCommentHash = hashScadCode(normalized);
      let combinedGeomHash = geometryHash(normalized);
      for (const f of workerFilesForHash) {
        const fileStr =
          typeof f.content === 'string'
            ? f.content
            : // Binary files: hash the byte length + filename as a proxy
              `${f.filename}:${(f.content as ArrayBuffer).byteLength}`;
        const fHash = hashScadCode(fileStr);
        // Mix file hash into both combined hashes using XOR + FNV prime
        combinedCommentHash =
          (Math.imul(combinedCommentHash ^ fHash, 0x01000193) >>> 0);
        combinedGeomHash =
          (Math.imul(combinedGeomHash ^ fHash, 0x01000193) >>> 0);
      }

      // Two hash levels (now incorporating project file contents):
      //   combinedCommentHash — processed code (comments stripped) + project files
      //   combinedGeomHash    — processed code (comments + scene extensions stripped) + project files
      const currentCommentHash = combinedCommentHash;
      const currentNormHash = combinedGeomHash;

      if (currentCommentHash === prevNormalizedHashRef.current) {
        // Only comments / blank lines changed — no WASM needed, no scene update needed.
        console.log(
          '[OpenSCADViewer] Skipping compile: comment-only edit detected',
        );
        return;
      }

      // -----------------------------------------------------------------------
      // Optimization 2: Scene-extension-only edit guard
      //
      // Compute a hash of the code with all Meshpit scene-extension module
      // calls removed (in addition to comments). If this hash matches the
      // previous geometry hash, only lights/PiP/scene settings changed.
      // The JS scene parser already updated the Three.js scene instantly —
      // no WASM compile needed.
      // -----------------------------------------------------------------------
      if (currentNormHash === prevGeometryHashRef.current) {
        // Only lights / PiP cameras / scene settings changed.
        // The JS parser already handled these; no WASM needed.
        console.log(
          '[OpenSCADViewer] Skipping compile: scene-extension-only edit detected',
        );
        // Update the comment hash so next comment-only edit is also caught.
        prevNormalizedHashRef.current = currentCommentHash;
        return;
      }

      // Geometry changed — update both hashes and dispatch a full compile.
      prevNormalizedHashRef.current = currentCommentHash;
      prevGeometryHashRef.current = currentNormHash;

      // Mark WASM result as stale so JS-parsed values take over immediately
      // (the context was already updated by the JS parser effect above).
      wasmResultFreshRef.current = false;

      // Non-JSON project files still need to be written to the WASM virtual FS
      // (STL, SVG, DXF, .scad, etc.). JSON files are already inlined into the
      // processed code and do not need to be present on the FS.
      const workerFiles = stableProjectFiles
        .filter((f) => !f.filename.toLowerCase().endsWith('.json'))
        .map((f) => ({
          filename: f.filename,
          content: f.content,
          isBinary: f.isBinary,
        }));
      compileScadRef.current(
        applyCodeSubstitutions(processedScadCode, parametersRef.current),
        workerFiles,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedScadCode, stableProjectFiles, isFetchingApi]);

  const setModelColor = usePerformanceStore((s) => s.setModelColor);

  // Update blob context when output arrives (for downloads)
  useEffect(() => {
    if (output && output instanceof Blob) {
      setBlob(output);

      if (coloredOutputs && coloredOutputs.length > 0) {
        setColoredBlobs(
          coloredOutputs.map((co) => ({
            color: co.color,
            blob: co.blob,
            isGhost: co.isGhost,
            excludedFromExport: co.excludedFromExport,
          })),
        );
        // Pick the first non-ghost color as the dominant model color for the
        // performance store (used to tint the worker activity graph).
        const dominant =
          coloredOutputs.find((co) => !co.isGhost) ?? coloredOutputs[0];
        setModelColor(dominant.color);
      } else {
        setColoredBlobs([]);
        setModelColor(null);
      }
    }
  }, [output, coloredOutputs, setBlob, setColoredBlobs, setModelColor]);

  // Error propagation
  useEffect(() => {
    if (onError) {
      onError(isError ? error || null : null);
    }

    if (isError && error) {
      console.log('OpenSCAD Error:', error);
      if (isSerializedOpenSCADError(error)) {
        console.log('Error stdErr:', error.stdErr);
      }
    }
  }, [isError, error, onError]);

  // Parse errors when available
  const parsed: ParsedOpenSCADErrors | null =
    isError && isSerializedOpenSCADError(error)
      ? parseOpenSCADErrors(error.stdErr, error.stdOut ?? [])
      : null;

  const stdErr: string[] = isSerializedOpenSCADError(error) ? error.stdErr : [];

  const hasGeometry = !!(geometry || coloredGeometries.length > 0);

  return (
    <div className="relative h-full w-full bg-muted/50 transition-all duration-300 ease-in-out">
      <div className="h-full w-full">
        {/* ThreeScene is always mounted to preserve the WebGL context and keep
            texture-script workers alive across compile transitions. */}
        <div
          className="h-full w-full"
          style={{ visibility: hasGeometry ? 'visible' : 'hidden' }}
        >
          <ThreeScene
            geometry={geometry ?? undefined}
            coloredGeometries={coloredGeometries}
            clones={clones}
            lights={lights}
            pipCameras={effectivePipCameras}
            cameraPath={effectiveCameraPath}
            svgOverlays={jsParsedSvgOverlays}
            isCompiling={isCompiling}
            onFitToBoundsReady={onFitToBoundsReady}
            autoFit={autoFit}
            isInitialFit={isInitialFit}
            onFitComplete={onFitComplete}
            productMode={productMode}
            showcaseMode={showcaseMode}
            cameraOffsetX={cameraOffsetX}
            presentationMode={externalPresentationMode}
            projectFiles={projectFiles}
            parameters={parameters}
            viewerState={controlledViewerState}
            onViewerStateChange={onControlledViewerStateChange}
            onInsertCode={onInsertCode}
            onReplaceCode={onReplaceCode}
            onInteraction={onInteraction}
            hiddenColorKeys={hiddenColorKeys}
            onSubscribeReady={onSubscribeReady}
            onPubSubPublish={onPubSubPublish}
          />
          {/* Right-side status overlays: compile errors + constraint warnings */}
          {(isError || constraintViolations.length > 0) && (
            <div className="absolute right-4 top-4 z-10 flex flex-col gap-2">
              {isError && parsed && (
                <div className="flex max-w-[400px] flex-col rounded-md bg-red-500/90 px-3 py-2 text-sm text-white shadow-lg backdrop-blur-sm">
                  <ErrorPanel
                    parsed={parsed}
                    rawStdErr={stdErr}
                    onJumpToLine={onJumpToLine}
                  />
                </div>
              )}
              {isError && !parsed && error && (
                <div className="flex max-w-[400px] flex-col rounded-md bg-red-500/90 px-3 py-2 text-sm text-white shadow-lg backdrop-blur-sm">
                  <span className="font-bold">Compilation Error</span>
                  <span className="mt-1 line-clamp-4 break-words text-xs opacity-90">
                    {(error as Error).message}
                  </span>
                </div>
              )}
              <ConstraintWarningPanel violations={constraintViolations} />
            </div>
          )}
        </div>

        {/* Error/empty overlay shown only when there is no geometry yet */}
        {!hasGeometry && isError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-red-500">
            {parsed ? (
              <div className="w-full max-w-md rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-left text-sm">
                <ErrorPanel
                  parsed={parsed}
                  rawStdErr={stdErr}
                  onJumpToLine={onJumpToLine}
                />
              </div>
            ) : (
              <>
                <p className="font-semibold">Error compiling SCAD</p>
                {error instanceof Error && (
                  <p className="text-sm opacity-80">{error.message}</p>
                )}
              </>
            )}
          </div>
        )}
        {(isCompiling || isFetchingApi) && (
          <div className="absolute right-4 top-4 z-10 flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-1.5 backdrop-blur-sm">
            <Loader2 className="h-3 w-3 animate-spin text-sky-500" />
            <p className="text-xs font-medium text-foreground/90">
              {isFetchingApi
                ? 'Fetching API data...'
                : progress
                  ? progress.message
                  : 'Updating...'}
            </p>
          </div>
        )}
        {apiFetchErrors.length > 0 && (
          <div className="absolute bottom-4 right-4 z-10 flex max-w-[420px] flex-col gap-1 rounded-md bg-yellow-500/90 px-3 py-2 text-xs text-white shadow-lg backdrop-blur-sm">
            <span className="flex items-center gap-1.5 font-bold">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              API include error{apiFetchErrors.length > 1 ? 's' : ''}
            </span>
            {apiFetchErrors.map((e) => (
              <span key={e} className="break-all font-mono opacity-90">
                {e}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
